import { observer } from "mobx-react";
import { ChangeEvent, useCallback, useState } from "react";
import { useStores } from "../../stores/root-store";
import { IVoucher } from "../../stores/voucher";
import { ApiResponse } from "../../stores";
import { getDiscountedValue, ShowAlert } from "../../utils/functions";

interface IApplyVouchers {
  orderValue: number;
  callback: (voucher: IVoucher | undefined) => void;
}

const toastTimeout = 2500;

const ApplyVouchers = observer(({ callback, orderValue }: IApplyVouchers) => {
  const { voucherStore } = useStores();
  const { applyVoucher } = voucherStore;
  const [voucherCode, setVoucherCode] = useState("");
  const [applied, setApplied] = useState(false);

  const handleInputChange = (evt: ChangeEvent<HTMLInputElement>) => {
    const { value } = evt.target;
    setVoucherCode(value);
  };

  const applyVoucherCallback = useCallback(
    (res: ApiResponse<IVoucher>) => {
      const { isSuccess, data, message } = res;
      if (isSuccess && data) {
        if (orderValue < data.minOrderValue) {
          ShowAlert.Error(
            `A minimum order value of £${data.minOrderValue} is required to apply this voucher.`,
            toastTimeout
          );
          return;
        }
        const [finalOrderAmount] = getDiscountedValue(
          orderValue,
          data.discountType,
          data.discountValue
        );
        if (finalOrderAmount <= 1) {
          ShowAlert.Error(
            `Order value does not meet the minimum requirement for this voucher.`,
            toastTimeout
          );
          return;
        }

        callback(data);
        setApplied(true);
        ShowAlert.Success("Voucher applied");
      } else
        ShowAlert.Error(
          message || "This voucher code is not valid or expired",
          toastTimeout
        );
    },
    [callback, orderValue]
  );

  const handleApplyVoucher = () => {
    applyVoucher(voucherCode, applyVoucherCallback);
  };

  const handleRemoveVoucher = () => {
    callback(undefined);
    setVoucherCode("");
    setApplied(false);
  };

  return (
    <>
      <h6>Apply Voucher</h6>
      <div className="position-relative">
        <input
          type="text"
          className="form-control pe-5"
          id="voucherCode"
          placeholder="Voucher Code"
          value={voucherCode}
          onChange={handleInputChange}
          autoComplete="off"
          disabled={applied}
        />
        {applied ? (
          <button
            className="btn btn-link position-absolute top-50 end-0 translate-middle-y me-2 p-0 text-danger opacity-75 hover-opacity-100"
            type="button"
            style={{ textDecoration: "none" }}
            onClick={handleRemoveVoucher}
          >
            Remove
          </button>
        ) : (
          <button
            className={`btn btn-link position-absolute top-50 end-0 translate-middle-y me-2 p-0 text-secondary opacity-75 hover-opacity-100${
              !voucherCode ? " disabled" : ""
            }`}
            style={{ textDecoration: "none" }}
            type="button"
            onClick={handleApplyVoucher}
          >
            Apply
          </button>
        )}
      </div>
    </>
  );
});

export default ApplyVouchers;
